import { useTranslation } from '@kostbar/i18n'
import { useSalesDetail } from '../../api/use-sales-query'
import { formatCurrency, formatDatetime } from '../../lib/format'
import {
  CHANNEL_BADGES,
  ORDER_STATUS_BADGES,
  PAYMENT_STATUS_BADGES,
} from './sales-config'
import type { BadgeMap } from '../../lib/types'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'

interface SalesDetailDrawerProps {
  saleId: string | null
  channel: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SalesDetailDrawer({
  saleId,
  channel,
  open,
  onOpenChange,
}: SalesDetailDrawerProps) {
  const { t } = useTranslation('reports')
  const { data, isLoading } = useSalesDetail(
    saleId ?? '',
    channel ?? '',
    open && !!saleId,
  )

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{t('detail.title')}</SheetTitle>
        </SheetHeader>

        {isLoading && (
          <div className="space-y-4 p-4">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {data && (
          <div className="space-y-6 p-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <BadgeFromMap value={data.channel} map={CHANNEL_BADGES} />
                <span className="text-sm text-muted-foreground">
                  {formatDatetime(data.occurredAt)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">
                    {t('detail.orderStatus')}
                  </span>
                  <div className="mt-1">
                    <BadgeFromMap
                      value={
                        data.orderStatus ??
                        (data.channel === 'pos' ? 'COMPLETED' : '')
                      }
                      map={ORDER_STATUS_BADGES}
                    />
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {t('detail.paymentStatus')}
                  </span>
                  <div className="mt-1">
                    <BadgeFromMap
                      value={data.paymentStatus ?? ''}
                      map={PAYMENT_STATUS_BADGES}
                    />
                  </div>
                </div>
                {data.operatorId && (
                  <div>
                    <span className="text-muted-foreground">
                      {t('detail.operator')}
                    </span>
                    <div className="mt-1 font-medium">{data.operatorId}</div>
                  </div>
                )}
                {data.stationId && (
                  <div>
                    <span className="text-muted-foreground">
                      {t('detail.station')}
                    </span>
                    <div className="mt-1 font-medium">{data.stationId}</div>
                  </div>
                )}
                {data.receiptNo && (
                  <div>
                    <span className="text-muted-foreground">
                      {t('detail.receipt')}
                    </span>
                    <div className="mt-1 font-mono text-xs">
                      {data.receiptNo}
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-md bg-muted p-3">
                <div className="text-sm text-muted-foreground">
                  {t('detail.gross')}
                </div>
                <div className="text-xl font-bold">
                  {formatCurrency(data.totalGrossCents)}
                </div>
              </div>
            </div>

            <div>
              <h4 className="mb-2 text-sm font-semibold">
                {t('detail.positions')}
              </h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('detail.item')}</TableHead>
                    <TableHead className="text-right">
                      {t('detail.qty')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('detail.unitPrice')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('detail.total')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div>{item.name}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {item.sku}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{item.qty}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(item.unitGrossCents)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(item.lineGrossCents)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {data.payments && data.payments.length > 0 && (
              <div>
                <h4 className="mb-2 text-sm font-semibold">
                  {t('detail.payments')}
                </h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('detail.provider')}</TableHead>
                      <TableHead>{t('detail.status')}</TableHead>
                      <TableHead className="text-right">
                        {t('detail.amount')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.payments.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.provider}</TableCell>
                        <TableCell>
                          <BadgeFromMap
                            value={p.status}
                            map={PAYMENT_STATUS_BADGES}
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(p.amountCents)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="text-xs text-muted-foreground">
              {t('detail.id')} <span className="font-mono">{data.id}</span>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

function BadgeFromMap({ value, map }: { value: string; map: BadgeMap }) {
  const entry = map[value]
  if (!entry) return <span className="text-sm">{value}</span>
  return <Badge variant={entry.variant ?? 'default'}>{entry.label}</Badge>
}
